import { useTheme } from '@shopify/restyle';
import { Box, Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import type { ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Pressable } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import { FlatList, ScrollView } from 'react-native-gesture-handler';
import type { Theme } from 'app/shopify';

export interface Tab {
  extraLabel?: string;
  extraLabelColor?: string;
  label?: string;
  image?: ReactElement;
  selectedImage?: ReactElement;
  selectedColor?: string;
  value: string;
  prefix?: ReactNode;
}

interface TabsProps<T extends Tab[]> {
  alignment?: 'center' | 'left';
  onPress?: (tab: T[number]['value']) => void;
  tabs: T;
  value: T[number]['value'];
  variant?: 'default' | 'pill' | 'toggle';
}

interface TabLayout {
  width: number;
  x: number;
}

function TabExtraLabel({
  extraLabel,
  extraLabelColor,
  foreground,
}: {
  extraLabel: string;
  extraLabelColor?: string;
  foreground: string;
}) {
  return (
    <Column
      borderRadius="radius.sm"
      justifyContent="center"
      minWidth={18}
      paddingHorizontal="spacing.general.x1"
      style={{
        backgroundColor: extraLabelColor ?? 'transparent',
        borderWidth: extraLabelColor ? 0 : 1,
        borderColor: `${foreground}33`,
      }}
    >
      <Text
        fontSize={11}
        fontWeight="600"
        style={{ textAlign: 'center', color: extraLabelColor ? 'white' : foreground }}
      >
        {extraLabel}
      </Text>
    </Column>
  );
}

// Extracted so that hooks are not called conditionally inside the parent component.
function SegmentedTabs({ onPress, tabs, value }: TabsProps<Tab[]>) {
  const theme = useTheme<Theme>();
  const activeIndex = tabs.findIndex((t) => t.value === value);
  const [tabLayouts, setTabLayouts] = useState<Partial<Record<string, TabLayout>>>({});
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const scrollViewRef = useRef<FlatList<Tab>>(null);
  const hasInitializedPillRef = useRef(false);

  const pillWidth = useSharedValue(0);
  const pillX = useSharedValue(0);

  const pillStyle = useAnimatedStyle(() => ({
    position: 'absolute',
    left: pillX.get(),
    width: pillWidth.get(),
  }));

  // Animate the pill whenever the active tab changes.
  useEffect(() => {
    const activeTab = tabs[activeIndex];
    const layout = activeTab ? tabLayouts[activeTab.value] : undefined;
    if (!layout) {
      return;
    }

    pillWidth.set(
      withSpring(layout.width, {
        damping: 20,
        stiffness: 200,
        mass: 0.4,
      }),
    );
    pillX.set(
      withSpring(layout.x, {
        damping: 20,
        stiffness: 200,
        mass: 0.4,
      }),
    );
  }, [activeIndex, pillWidth, pillX, tabLayouts, tabs]);

  useEffect(() => {
    const activeTab = tabs[activeIndex];
    const layout = activeTab ? tabLayouts[activeTab.value] : undefined;
    if (!layout || containerWidth <= 0) {
      return;
    }

    const maxScrollX = Math.max(0, contentWidth - containerWidth);
    const targetX = Math.min(Math.max(layout.x - (containerWidth - layout.width) / 2, 0), maxScrollX);

    scrollViewRef.current?.scrollToOffset({ offset: targetX, animated: true });
  }, [activeIndex, containerWidth, contentWidth, tabLayouts, tabs]);

  return (
    <FlatList
      ref={scrollViewRef}
      data={tabs}
      horizontal
      keyExtractor={(tab) => tab.value}
      ListHeaderComponent={
        <Box alignSelf="stretch" width={0}>
          <Animated.View
            pointerEvents="none"
            style={[
              {
                top: 0,
                bottom: 0,
                borderRadius: 100,
                backgroundColor: theme.colors['surface.secondary-neutral'],
              },
              pillStyle,
            ]}
          />
        </Box>
      }
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
      onContentSizeChange={(width) => setContentWidth(width)}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        padding: 3,
        // gap: theme.spacing.m,
        marginLeft: theme.spacing['spacing.general.x2'],
      }}
      style={{
        flexGrow: 0,
        flexShrink: 0,
        marginVertical: theme.spacing['spacing.general.x2'],
        position: 'relative',
      }}
      renderItem={({ item: tab }) => {
        const isActive = tab.value === value;
        return (
          <Pressable
            onLayout={(e) => {
              const nextLayout = e.nativeEvent.layout;

              setTabLayouts((currentLayouts) => {
                if (tab.value in currentLayouts) {
                  const currentLayout = currentLayouts[tab.value]!;
                  if (currentLayout.width === nextLayout.width && currentLayout.x === nextLayout.x) {
                    return currentLayouts;
                  }
                }

                return {
                  ...currentLayouts,
                  [tab.value]: {
                    width: nextLayout.width,
                    x: nextLayout.x,
                  },
                };
              });

              if (isActive && !hasInitializedPillRef.current) {
                hasInitializedPillRef.current = true;
                pillWidth.set(nextLayout.width);
                pillX.set(nextLayout.x);
              }
            }}
            onPress={() => onPress?.(tab.value)}
            style={{
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
              paddingVertical: theme.spacing['spacing.general.x0,5'],
              paddingHorizontal: theme.spacing['spacing.general.x2'],
              borderRadius: 100,
              zIndex: 1,
            }}
          >
            <Row alignItems="center" gap="spacing.general.x1" opacity={isActive ? 1 : 0.8}>
              {tab.prefix}
              <Text
                color={isActive ? 'text.primary' : 'text.secondary'}
                variant={isActive ? 'bodyBaseStrong' : 'bodyBaseMedium'}
              >
                {tab.label}
              </Text>
              {tab.extraLabel ? (
                <TabExtraLabel
                  extraLabel={tab.extraLabel}
                  extraLabelColor={tab.extraLabelColor}
                  foreground={theme.colors['text.primary']}
                />
              ) : null}
            </Row>
          </Pressable>
        );
      }}
    />
  );
}

function PillTabs({ alignment = 'center', onPress, tabs, value }: TabsProps<Tab[]>) {
  const theme = useTheme<Theme>();
  const contentContainerStyle = {
    paddingTop: theme.spacing['spacing.general.x2'],
    alignItems: 'center' as const,
    flexGrow: 1,
    gap: 10,
    justifyContent: alignment === 'left' ? ('flex-start' as const) : ('center' as const),
    paddingHorizontal: alignment === 'left' ? 0 : theme.spacing['spacing.general.x4'],
  };

  return (
    <FlatList
      data={tabs}
      horizontal
      contentContainerStyle={contentContainerStyle}
      showsHorizontalScrollIndicator={false}
      style={{
        backgroundColor: theme.colors['surface.default'],
        flexGrow: 0,
        flexShrink: 0,
      }}
      keyExtractor={(tab) => tab.value}
      renderItem={({ item: tab }) => {
        const isActive = tab.value === value;

        return (
          <Pressable
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive }}
            onPress={() => onPress?.(tab.value)}
            style={({ pressed }) => ({
              alignItems: 'center',
              backgroundColor: isActive
                ? theme.colors['component.button.bg.primary']
                : pressed
                  ? `${theme.colors.black}14`
                  : `${theme.colors.black}0D`,
              borderRadius: theme.borderRadii['radius.full'],
              flexDirection: 'row',
              flexShrink: 0,
              gap: 4,
              justifyContent: 'center',
              minHeight: 40,
              opacity: pressed ? 0.88 : 1,
              paddingHorizontal: 14,
              paddingVertical: 10,
            })}
            testID={`tab-${tab.value}`}
          >
            {tab.prefix}
            <Text
              fontSize={isActive ? 16 : 15}
              fontWeight={isActive ? '600' : '400'}
              numberOfLines={1}
              style={{
                color: isActive ? theme.colors['text.inverse.primary'] : theme.colors['text.primary'],
                letterSpacing: -0.32,
                lineHeight: 16,
              }}
            >
              {tab.label}
            </Text>
            {tab.extraLabel ? (
              <TabExtraLabel
                extraLabel={tab.extraLabel}
                extraLabelColor={tab.extraLabelColor}
                foreground={isActive ? theme.colors['text.inverse.primary'] : theme.colors['text.primary']}
              />
            ) : null}
          </Pressable>
        );
      }}
    />
  );
}

function ToggleTabs({ onPress, tabs, value }: TabsProps<Tab[]>) {
  const theme = useTheme<Theme>();

  return (
    <ScrollView
      horizontal
      contentContainerStyle={{
        alignItems: 'center',
        flexGrow: 1,
        justifyContent: 'center',
        paddingHorizontal: theme.spacing['spacing.general.x4'],
      }}
      showsHorizontalScrollIndicator={false}
      style={{ flexGrow: 0, flexShrink: 0, marginVertical: theme.spacing['spacing.general.x2'] }}
    >
      <Row backgroundColor="surface.primary-neutral" borderRadius="radius.full" padding="spacing.general.x1">
        {tabs.map((tab) => {
          const isActive = tab.value === value;

          return (
            <Pressable
              accessibilityRole="tab"
              accessibilityState={{ selected: isActive }}
              key={tab.value}
              onPress={() => onPress?.(tab.value)}
              style={({ pressed }) => ({
                alignItems: 'center',
                backgroundColor: isActive
                  ? theme.colors['surface.brand.strong']
                  : pressed
                    ? theme.colors['state-layer.press']
                    : 'transparent',
                borderRadius: theme.borderRadii['radius.full'],
                flexDirection: 'row',
                justifyContent: 'center',
                minHeight: 40,
                opacity: pressed ? 0.88 : 1,
                paddingHorizontal: theme.spacing['spacing.general.x3'],
                paddingVertical: theme.spacing['spacing.general.x2'],
              })}
              testID={`tab-${tab.value}`}
            >
              <Text
                color={isActive ? 'text.inverse.primary' : 'text.brand'}
                fontWeight="600"
                numberOfLines={1}
                variant="bodyBase"
              >
                {tab.label}
              </Text>
            </Pressable>
          );
        })}
      </Row>
    </ScrollView>
  );
}

function Tabs<T extends Tab[]>({ alignment, onPress, tabs, value, variant = 'default' }: TabsProps<T>) {
  const theme = useTheme<Theme>();
  const scrollViewRef = useRef<ScrollView>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [tabLayouts, setTabLayouts] = useState<Partial<Record<string, TabLayout>>>({});
  const activeTabLayout = tabLayouts[value];
  useEffect(() => {
    if (!activeTabLayout || containerWidth <= 0) {
      return;
    }

    const maxScrollX = Math.max(0, contentWidth - containerWidth);
    const targetX = Math.min(Math.max(activeTabLayout.x - (containerWidth - activeTabLayout.width) / 2, 0), maxScrollX);

    scrollViewRef.current?.scrollTo({ x: targetX, animated: true });
  }, [activeTabLayout, containerWidth, contentWidth]);

  if (variant === 'pill') {
    return <PillTabs alignment={alignment} onPress={onPress} tabs={tabs} value={value} />;
  }

  if (variant === 'toggle') {
    return <ToggleTabs onPress={onPress} tabs={tabs} value={value} />;
  }

  const allHaveImages = tabs.every((t) => Boolean(t.image));
  const noneHaveImages = tabs.every((t) => !t.image);
  const isIconTabs = allHaveImages;

  const useTextTabRowLayout = noneHaveImages;
  const fewTabsLayout = useTextTabRowLayout && tabs.length > 2 && tabs.length < 6;
  const deactivateSegmentedTabs = true;

  // Delegate to animated segmented control for the common case.
  // For now we keep the old tab layout.

  if (!deactivateSegmentedTabs && fewTabsLayout) {
    return <SegmentedTabs onPress={onPress} tabs={tabs} value={value} />;
  }

  const renderedTabs = tabs.map((tab) => (
    <Pressable
      key={tab.value}
      testID={`tab-${tab.value}`}
      onLayout={(e) => {
        const nextLayout = e.nativeEvent.layout;

        setTabLayouts((currentLayouts) => {
          const currentLayout = currentLayouts[tab.value];
          if (currentLayout?.width === nextLayout.width && currentLayout.x === nextLayout.x) {
            return currentLayouts;
          }

          return {
            ...currentLayouts,
            [tab.value]: {
              width: nextLayout.width,
              x: nextLayout.x,
            },
          };
        });
      }}
      onPress={() => onPress?.(tab.value)}
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        borderBottomWidth: tab.image ? 0 : 2,
        borderBottomColor:
          tab.value === value
            ? theme.colors['surface.brand.default']
            : theme.colors['surface.inverse.tertiary-neutral'],
        paddingHorizontal: theme.spacing['spacing.general.x3'],
        paddingVertical: tab.image ? theme.spacing['spacing.general.x1'] : theme.spacing['spacing.general.x4'],
        flex: 1,
      }}
    >
      {tab.image ? (
        <Box
          height={40}
          style={{
            backgroundColor: tab.value === value ? tab.selectedColor : theme.colors['surface.default'],
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          width={40}
        >
          {tab.value === value ? (tab.selectedImage ?? tab.image) : tab.image}
        </Box>
      ) : (
        <Row alignItems="center" gap={tab.extraLabel ? 'spacing.general.x1' : 'spacing.general.x2'}>
          {tab.prefix}
          <Text
            color={tab.value === value ? 'text.primary' : 'icon.tertiary'}
            fontWeight="700"
            variant="bodyBase"
            textAlign="center"
          >
            {tab.label}
          </Text>
          {tab.extraLabel ? (
            <TabExtraLabel
              extraLabel={tab.extraLabel}
              extraLabelColor={tab.extraLabelColor}
              foreground={theme.colors['text.primary']}
            />
          ) : null}
        </Row>
      )}
    </Pressable>
  ));

  const tabsRow = (
    <Row
      borderBottomColor="surface.disabled"
      flexWrap="nowrap"
      justifyContent="space-evenly"
      paddingVertical={isIconTabs ? 'spacing.general.x2' : 'spacing.general.0'}
      minWidth="100%"
    >
      {renderedTabs}
    </Row>
  );

  return (
    <ScrollView
      ref={scrollViewRef}
      horizontal
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
      onContentSizeChange={(width) => setContentWidth(width)}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ minWidth: '100%' }}
      style={{ flexGrow: 0, flexShrink: 0 }}
    >
      {tabsRow}
    </ScrollView>
  );
}

export { Tabs };
